import './styles/blogArchives.scss';

import {
    DroletButton,
    Pagination,
    SingleBlog,
    TitleBanner,
} from '../components/Primitives';
import React, { useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IBlog } from '../interfaces/interfaces';
import { Input } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Primitives';
import Layout from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Layout/Layout';
import SEO from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Seo';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { graphql } from 'gatsby';
import useMobileDetect from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Hooks/useMobileDetect';
import { usePageLocalization } from '@sbi-international/boutik.gatsby.plugin.multilocale';
import { useTranslation } from 'react-i18next';

export const query = graphql`
    query {
        listingCms: allProductsJson(filter: { code: { eq: "drolet_blogs" } }) {
            nodes {
                ...cmsDataFragment
            }
        }
        recentBlogCms: allProductsJson(
            filter: { code: { eq: "drolet_blog-recent" } }
        ) {
            nodes {
                ...cmsDataFragment
            }
        }
        blogs: allProductsJson(
            filter: { category: { eq: "/Drolet/CMS/Blog/" } }
        ) {
            nodes {
                code
                title {
                    en_CA
                    en_US
                    fr_CA
                }
                attributes {
                    blog_date {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    content {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                        valuesMarkdownRemark {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    blog_subject {
                        group
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    recent_article {
                        values {
                            fr_CA
                            en_CA
                            en_US
                        }
                    }
                }
                fields {
                    ...productsFieldsImagesFragment
                }
                slug {
                    en_CA
                    en_US
                    fr_CA
                }
            }
        }
        metadata: allProductsJson(filter: { code: { eq: "drolet_blogs" } }) {
            nodes {
                code
                attributes {
                    meta_title {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    meta_description {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                }
            }
        }
    }
`;

const BlogArchives = ({ data }) => {
    const { t } = useTranslation('pages');
    const { currentLocale } = usePageLocalization();
    const isMobile = useMobileDetect().isMobile();
    const [page, setPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const responsiveImages = data?.listingCms?.nodes?.[0]?.fields;
    const desktopImage =
        responsiveImages?.banniere_desktop[0]?.childImageSharp?.gatsbyImageData;
    const mobileImage =
        responsiveImages?.banniere_mobile[0]?.childImageSharp?.gatsbyImageData;
    const pageTitle = data?.listingCms?.nodes?.[0]?.title?.[currentLocale];
    const blogsPerPage = 9;
    const listingCms = data?.listingCms?.nodes?.[0];
    const blogs: IBlog[] = data?.blogs?.nodes;
    let categories = blogs.map(
        (blog) => blog.attributes?.blog_subject?.values[0]?.[currentLocale]
    );
    categories = categories.filter(
        (x, i, a) => a.indexOf(x) === i && x?.trim !== undefined
    );

    const [filteredBlogs, setFilteredBlogs] = useState(blogs);
    const recentBlogCms = data?.recentBlogCms?.nodes?.[0];
    const recentBlog = blogs.find(
        (blog) => blog?.attributes?.recent_article !== null
    );
    const [blogCategory, setBlogCategory] = useState('all');

    const searchAction = () => {
        setFilteredBlogs(
            blogs.filter((blog) => {
                return (
                    blog.title[currentLocale]
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase()) ||
                    blog.attributes?.content?.values[0]?.[currentLocale]
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase())
                );
            })
        );
    };
    const metadataAttributes = data?.metadata?.nodes?.[0]?.attributes;

    return (
        <Layout>
            <SEO
                title={
                    metadataAttributes.meta_title?.values?.[0][currentLocale]
                }
                description={
                    metadataAttributes.meta_description?.values?.[0][
                        currentLocale
                    ]
                }
            />
            <TitleBanner
                className='blogs-titlebanner'
                imageData={isMobile ? mobileImage : desktopImage}
                title={pageTitle}
                content={
                    listingCms?.attributes?.content
                        ?.valuesMarkdownRemark?.[0]?.[currentLocale]
                }
            />
            <div className='main-blog-bg' />
            <div className='main-blog container'>
                <div className='main-blog-content'>
                    {recentBlog && (
                        <>
                            <div className='main-blog-title'>
                                <span className='main-sub-title'>
                                    {recentBlogCms?.attributes?.texte_1
                                        ?.values?.[0]?.[currentLocale] || ''}
                                </span>
                                <span className='main-title'>
                                    {recentBlogCms?.attributes?.texte_2
                                        ?.values?.[0]?.[currentLocale] || ''}
                                </span>
                                <span className='line'></span>
                            </div>
                            <div className='recent-blog'>
                                <SingleBlog
                                    blog={recentBlog}
                                    displayFaqBloc={true}
                                />
                            </div>
                        </>
                    )}
                </div>
            </div>
            <div className='blog-page container'>
                <div className='blog-categories'>
                    <DroletButton
                        label={t('blogs.all') as string}
                        className={blogCategory === 'all' ? 'selected' : ''}
                        onClick={() => {
                            setBlogCategory('all');
                            setFilteredBlogs(blogs);
                        }}
                    />
                    {categories.map((category, index) => (
                        <DroletButton
                            key={index}
                            label={category}
                            className={
                                blogCategory === category ? 'selected' : ''
                            }
                            onClick={() => {
                                setBlogCategory(category);
                                setFilteredBlogs(
                                    blogs.filter(
                                        (blog) =>
                                            blog?.attributes?.blog_subject
                                                ?.values[0]?.[currentLocale] ===
                                            category
                                    )
                                );
                            }}
                        />
                    ))}
                </div>
                <div className='blog-search-bar'>
                    <Input
                        onChange={(value) => setSearchTerm(value)}
                        onKeyDown={(key) => {
                            if (key === 'Enter' && searchTerm.trim() !== '') {
                                searchAction();
                            } else {
                                setFilteredBlogs(blogs);
                            }
                        }}
                        placeholder={t('layout:header.search-placeholder') as string}
                    />
                    <FontAwesomeIcon
                        className='icon'
                        icon={faSearch}
                        onClick={() => {
                            if (searchTerm.trim() !== '') {
                                searchAction();
                            } else {
                                setFilteredBlogs(blogs);
                            }
                        }}
                    />
                </div>
                <div className='blog-section'>
                    {filteredBlogs &&
                        filteredBlogs.length > 0 &&
                        filteredBlogs?.sort((a: any, b: any) => {
                                const dateA = new Date(
                                    a.attributes.blog_date.values[0].en_CA
                                );
                                const dateB = new Date(
                                    b.attributes.blog_date.values[0].en_CA
                                );
                                return dateB.getTime() - dateA.getTime(); // tri décroissant
                            }).slice(
                                blogsPerPage * (page - 1),
                                blogsPerPage * page
                            )
                            ?.map((blog, index) => {
                                return (
                                    
                                    <SingleBlog
                                        blog={blog}
                                        key={`blog-archives-${index}`}
                                    />
                                    
                                );
                            })}
                </div>
            </div>
            {filteredBlogs && filteredBlogs.length / blogsPerPage > 1 && (
                <Pagination
                    currentPage={page}
                    onPageChange={(newPage: number) => setPage(newPage)}
                    pageNumber={Math.ceil(filteredBlogs.length / blogsPerPage)}
                />
            )}
        </Layout>
    );
};
export default BlogArchives;
